export const MISSION_NODE_ID = "mission_root";
export const ORPHAN_PROJECTS_SECTION_ID = "orphan_projects_section";
export const ORPHAN_GOALS_SECTION_ID = "orphan_goals_section";

// normalizeGraph turns raw goal/project entities into graph items ready for
// buildGraph + autoLayoutNodes. It is a pure function: it never mutates the
// input entities or their mapX/mapY; filtering is done by the caller BEFORE this
// runs, so toggling a filter only rebuilds the derived graph, never the source data.
//
// Structural rules enforced here (the graph-level split into mainTree vs
// orphan-projects is re-validated by buildGraph via BFS from the mission):
//  1. The mission is the only root: it has no parent and no node can be its parent.
//  2. Every goal keeps a valid directed path from the mission. When a goal's
//     parent chain is broken or hidden it is attached directly to the mission
//     (synthetic edge) so it always stays inside the main tree.
//  3. Only projects can be orphaned. A project is pushed to the "orphan-projects"
//     section only when it has no visible goal (or goal ancestor) and no visible
//     parent project in its chain; projects never get a fake mission parent.
//  4. Self-loops are impossible: a node is never connected to itself.
export function normalizeGraph(goals, projects, mission, source = {}) {
    const items = [];
    let x = 80, y = 80;
    const allGoals = source.allGoals || goals || [];
    const visibleGoalIds = new Set((goals || []).map(goalItem => String(goalItem.id)));
    const visibleProjectIds = new Set((projects || []).map(projectItem => String(projectItem.id)));
    const goalsById = (allGoals || []).reduce((acc, goalItem) => ({
        ...acc,
        [String(goalItem.id)]: goalItem,
    }), {});

    const bump = () => ({ x: (x += 40), y: (y += 40) });
    const pos = (mapX, mapY) =>
        Number.isFinite(+mapX) && Number.isFinite(+mapY)
            ? { x: +mapX, y: +mapY }
            : bump();

    // Walk a goal item's parentGoal chain (including the item itself) and return
    // the nearest VISIBLE goal as a graph id, or undefined when the chain has no
    // visible member. `visited` (seeded with the goal's own id) guards against
    // malformed/self-referencing chains so a node can never parent itself.
    const resolveNearestVisibleGoal = (goalItem, selfId) => {
        let current = goalItem;
        const visited = new Set(selfId ? [String(selfId)] : []);

        while (current?.id && !visited.has(String(current.id))) {
            const currentId = String(current.id);

            if (visibleGoalIds.has(currentId)) {
                return `goal_${currentId}`;
            }

            visited.add(currentId);
            current = goalsById[currentId]?.parentGoal;
        }

        return undefined;
    };
    // A goal must always stay inside the main tree: when its parent chain is
    // broken/hidden it becomes a direct child of the mission (synthetic edge).
    const resolveVisibleGoalParentId = (goalItem) => (
        resolveNearestVisibleGoal(goalItem?.parentGoal, goalItem?.id) || MISSION_NODE_ID
    );
    const isNaturalRootGoal = (goalItem) => (
        !goalItem?.parentGoal?.id && !goalItem?.parentGoal?.documentId
    );
    const resolveVisibleProjectParentIds = (projectItem) => {
        // Decided architecture: single-parent tree layout. parentProjects come
        // before goals so the project renders under its project parent when both
        // kinds of edges exist; the remaining parents are rendered as dashed
        // (layout:false) edges and never affect tree positioning.
        const parentIds = [];
        const addParentId = (parentId) => {
            if (parentId && parentId !== `project_${projectItem.id}` && !parentIds.includes(parentId)) {
                parentIds.push(parentId);
            }
        };
        (projectItem.parentProjects || []).forEach(parentProject => {
            if (visibleProjectIds.has(String(parentProject?.id))) {
                addParentId(`project_${parentProject.id}`);
            }
        });
        (projectItem.goals || []).forEach(goalItem => {
            // No mission fallback for projects: if the goal chain has no visible
            // member this project simply contributes no parent and becomes orphan.
            addParentId(resolveNearestVisibleGoal(goalItem));
        });

        return parentIds;
    };

    items.push({
        id: MISSION_NODE_ID,
        type: "mission",
        parentId: null,
        parentIds: [],
        position: { x: 320, y: 20 },
        draggable: false,
        entity: {
            id: MISSION_NODE_ID,
            title: mission,
        },
    });

    for (const g of goals || []) {
        const goalId = `goal_${g.id}`;
        // A goal created/detached without a parent is not attached to the mission: it lives in the
        // "goals without parent" section (like projects without a goal).
        const isOrphanRoot = Boolean(g.isOrphan) && isNaturalRootGoal(g);
        const parentId = isOrphanRoot ? null : resolveVisibleGoalParentId(g);

        items.push({
            id: goalId,
            type: "goal",
            parentId,
            parentIds: [],
            section: isOrphanRoot ? "orphan-goals" : undefined,
            position: pos(g.mapX, g.mapY),
            entity: g,
            synthetic: !isNaturalRootGoal(g) && parentId === MISSION_NODE_ID,
        });
    }

    const projectItems = (projects || []).map((p) => {
        const projectId = `project_${p.id}`;
        const parentIds = resolveVisibleProjectParentIds(p);

        return {
            id: projectId,
            type: "project",
            parentId: parentIds[0] || null,
            parentIds: parentIds.slice(1),
            section: parentIds.length ? undefined : "orphan-projects",
            position: pos(p.mapX, p.mapY),
            entity: p,
        };
    });

    items.push(...projectItems);

    return items;
}
